import { Spin, Typography } from 'antd'
import { useCdfRawValues2DArray, useColorScale } from 'features/dataLayers'
import { convertFfs } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useGeckoLocale } from 'features/locale'
import Plot from 'features/plotly'
import React, { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  PipeOrientation,
} from 'types'

const { Text } = Typography

// Linear scalar factor between two units, used to scale whole heatmap arrays and
// click coordinates cheaply. Valid here because thickness and geometric positions
// are length units (offset-free), so convert(1) is an exact multiplier.
const unitFactor = (
  from: GeckoMeasurementUnit,
  to: GeckoMeasurementUnit
): number => convertFfs(1, from, to)

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`

const HeatmapContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  overflow: hidden;
  cursor: crosshair;
`

const SelectedValueDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: ${({ theme }) => theme.forecast.glassBackground};
  border-radius: 0.25rem;
  min-height: 1.5rem;
`

// Fallback color scale if we can't get one from the context
const FALLBACK_COLOR_SCALE: [number, string][] = [
  [0, '#d32f2f'],
  [0.25, '#ff9800'],
  [0.5, '#ffeb3b'],
  [0.75, '#8bc34a'],
  [1, '#4caf50'],
]

export interface SelectedBinValue {
  binX: number
  binY: number
  measurement: GeckoMeasurement
}

export interface InspectionHeatmapPickerProps {
  cdf: GeckoComponentDataFrame | undefined
  region: GeckoComponentRect | undefined
  selectedBin: SelectedBinValue | null
  onBinSelect: (bin: SelectedBinValue | null) => void
  title: string
  width?: number
  height?: number
  /** Original indication region (for boundary selection mode) */
  originalRegion?: GeckoComponentRect
  /** Enable boundary selection mode (only perimeter) */
  boundarySelectionMode?: boolean
}

/**
 * A heatmap component that displays thickness data from a CDF and allows
 * the user to click on a bin to select its value.
 */
export const InspectionHeatmapPicker: FC<InspectionHeatmapPickerProps> = ({
  cdf,
  region,
  selectedBin,
  onBinSelect,
  title,
  width = 250,
  height = 250,
  originalRegion,
  boundarySelectionMode = false,
}) => {
  const { colorScaleThreshold, assetModel, component } = useIndicationData()

  // For horizontal pipes, swap axes so length goes left-right and circumference goes up-down
  const shouldSwapAxes = useMemo(() => {
    if (!assetModel || !component) return false
    const componentModel = assetModel.data.components.get(component.id)
    if (!componentModel) return false

    if ('orientation' in componentModel) {
      return componentModel.orientation === PipeOrientation.Horizontal
    }
    return false
  }, [assetModel, component])

  // Display units follow the global measurement system. z (thickness) and the two
  // geometric axes each get their own preferred unit; selection math below stays
  // in the CDF/region SOURCE units — only what's shown to the user is converted.
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const zSourceUnit =
    cdf?.data.params.measurement_unit ?? GeckoMeasurementUnit.Millimeters
  const zDisplayUnit = getPreferredMeasurementUnit(zSourceUnit)
  const zFactor = unitFactor(zSourceUnit, zDisplayUnit)

  const xAxisSourceUnit = shouldSwapAxes ? region?.y.unit : region?.x.unit
  const yAxisSourceUnit = shouldSwapAxes ? region?.x.unit : region?.y.unit
  const xAxisDisplayUnit = xAxisSourceUnit
    ? getPreferredMeasurementUnit(xAxisSourceUnit)
    : undefined
  const yAxisDisplayUnit = yAxisSourceUnit
    ? getPreferredMeasurementUnit(yAxisSourceUnit)
    : undefined
  const xFactor =
    xAxisSourceUnit && xAxisDisplayUnit
      ? unitFactor(xAxisSourceUnit, xAxisDisplayUnit)
      : 1
  const yFactor =
    yAxisSourceUnit && yAxisDisplayUnit
      ? unitFactor(yAxisSourceUnit, yAxisDisplayUnit)
      : 1

  const { colorScale, colorScaleMin, colorScaleMax } = useColorScale(
    cdf,
    colorScaleThreshold
  )

  // Convert GeckoColorScale to Plotly colorscale format
  const plotlyColorScale = useMemo((): [number, string][] => {
    if (
      !colorScale ||
      typeof colorScale.getColorForValue !== 'function' ||
      colorScaleMin === undefined ||
      colorScaleMax === undefined
    ) {
      return FALLBACK_COLOR_SCALE
    }

    const numPoints = 10
    const range = colorScaleMax - colorScaleMin
    if (range === 0) return FALLBACK_COLOR_SCALE

    const scale: [number, string][] = []
    for (let i = 0; i <= numPoints; i++) {
      const normalizedValue = i / numPoints
      const actualValue = colorScaleMin + normalizedValue * range
      const color = colorScale.getColorForValue(actualValue)
      scale.push([normalizedValue, color])
    }
    return scale
  }, [colorScale, colorScaleMin, colorScaleMax])

  const { array: data, isLoading } = useCdfRawValues2DArray(cdf, region)

  // Calculate min/max from data for fallback color scale
  const { dataMin, dataMax } = useMemo(() => {
    if (!data || data.length === 0) {
      return { dataMin: undefined, dataMax: undefined }
    }

    let min = Infinity
    let max = -Infinity

    for (const row of data) {
      for (const value of row) {
        if (!isNaN(value) && isFinite(value)) {
          if (value < min) min = value
          if (value > max) max = value
        }
      }
    }

    return {
      dataMin: min === Infinity ? undefined : min,
      dataMax: max === -Infinity ? undefined : max,
    }
  }, [data])

  const { xPositions, yPositions } = useMemo(() => {
    if (!region || !data || data.length === 0) {
      return { xPositions: [], yPositions: [] }
    }

    const numRows = data.length
    const numCols = data[0]?.length ?? 0

    const regionXStart = region.start.x.num
    const regionXEnd = region.end.x.num
    const regionYStart = region.start.y.num
    const regionYEnd = region.end.y.num

    const xPos: number[] = []
    for (let i = 0; i < numCols; i++) {
      const pos =
        regionXStart + (i / (numCols - 1 || 1)) * (regionXEnd - regionXStart)
      xPos.push(pos)
    }

    const yPos: number[] = []
    for (let i = 0; i < numRows; i++) {
      const pos =
        regionYStart + (i / (numRows - 1 || 1)) * (regionYEnd - regionYStart)
      yPos.push(pos)
    }

    if (shouldSwapAxes) {
      return {
        xPositions: yPos,
        yPositions: xPos,
      }
    }

    return { xPositions: xPos, yPositions: yPos }
  }, [region, data, shouldSwapAxes])

  const isInOriginalRegion = useCallback(
    (posX: number, posY: number): boolean => {
      if (!boundarySelectionMode || !originalRegion) return false

      const xInRange =
        posX >= originalRegion.start.x.num && posX <= originalRegion.end.x.num
      const yInRange =
        posY >= originalRegion.start.y.num && posY <= originalRegion.end.y.num

      return xInRange && yInRange
    },
    [boundarySelectionMode, originalRegion]
  )

  const handleClick = useCallback(
    (event: Plotly.PlotMouseEvent) => {
      if (
        !event.points ||
        event.points.length === 0 ||
        !cdf ||
        !data ||
        !region
      )
        return

      const point = event.points[0]
      // Coordinates come back in DISPLAY units; convert them back to source units
      // so the region-based bin math (all in source units) is unchanged.
      const clickXDisplay =
        typeof point.x === 'number' ? point.x : Number(point.x)
      const clickYDisplay =
        typeof point.y === 'number' ? point.y : Number(point.y)
      const clickX = xFactor !== 0 ? clickXDisplay / xFactor : clickXDisplay
      const clickY = yFactor !== 0 ? clickYDisplay / yFactor : clickYDisplay
      // Raw source-unit thickness is carried in customdata (z is display-converted).
      const rawZ =
        'customdata' in point && typeof point.customdata === 'number'
          ? point.customdata
          : 'z' in point && typeof point.z === 'number'
            ? point.z
            : NaN
      const value = rawZ

      if (isNaN(value) || isNaN(clickX) || isNaN(clickY)) {
        onBinSelect(null)
        return
      }

      const numRows = data.length
      const numCols = data[0]?.length ?? 0
      const regionXStart = region.start.x.num
      const regionXEnd = region.end.x.num
      const regionYStart = region.start.y.num
      const regionYEnd = region.end.y.num

      // If axes are swapped, clickX/Y correspond to swapped positions
      const posX = shouldSwapAxes ? clickY : clickX
      const posY = shouldSwapAxes ? clickX : clickY

      // In boundary selection mode, reject clicks inside the original region
      if (isInOriginalRegion(posX, posY)) {
        onBinSelect(null)
        return
      }

      const binX = Math.round(
        ((posX - regionXStart) / (regionXEnd - regionXStart || 1)) *
          (numCols - 1)
      )
      const binY = Math.round(
        ((posY - regionYStart) / (regionYEnd - regionYStart || 1)) *
          (numRows - 1)
      )

      onBinSelect({
        binX,
        binY,
        measurement: GeckoMeasurement.CreateFromValue(
          value,
          cdf.data.params.measurement_unit
        ),
      })
    },
    [
      cdf,
      data,
      region,
      onBinSelect,
      shouldSwapAxes,
      isInOriginalRegion,
      xFactor,
      yFactor,
    ]
  )

  const selectedMarker = useMemo(() => {
    if (!selectedBin || !data || !region) return null

    const numRows = data.length
    const numCols = data[0]?.length ?? 0
    const regionXStart = region.start.x.num
    const regionXEnd = region.end.x.num
    const regionYStart = region.start.y.num
    const regionYEnd = region.end.y.num

    const posX =
      regionXStart +
      (selectedBin.binX / (numCols - 1 || 1)) * (regionXEnd - regionXStart)
    const posY =
      regionYStart +
      (selectedBin.binY / (numRows - 1 || 1)) * (regionYEnd - regionYStart)

    // Positions are computed in source space, then scaled to the display axes.
    const markerX = (shouldSwapAxes ? posY : posX) * xFactor
    const markerY = (shouldSwapAxes ? posX : posY) * yFactor

    return {
      x: [markerX],
      y: [markerY],
      mode: 'markers' as const,
      type: 'scatter' as const,
      marker: {
        size: 12,
        color: 'white',
        symbol: 'circle-open',
        line: { width: 3, color: 'white' },
      },
      hoverinfo: 'skip' as const,
    }
  }, [selectedBin, data, region, shouldSwapAxes, xFactor, yFactor])

  // Source-unit z, transposed to match the displayed axes. Used as customdata so
  // the click handler recovers the raw thickness regardless of display unit.
  const rawZData = useMemo(() => {
    if (!data) return undefined

    if (shouldSwapAxes) {
      const rows = data.length
      const cols = data[0]?.length ?? 0
      const transposed: number[][] = []
      for (let c = 0; c < cols; c++) {
        const newRow: number[] = []
        for (let r = 0; r < rows; r++) {
          newRow.push(data[r][c])
        }
        transposed.push(newRow)
      }
      return transposed
    }

    return data.map((row) => [...row])
  }, [data, shouldSwapAxes])

  // Display-unit z (what the heatmap renders and its hover shows).
  const displayZData = useMemo(() => {
    if (!rawZData) return undefined
    return rawZData.map((row) =>
      row.map((v) => (Number.isFinite(v) ? v * zFactor : v))
    )
  }, [rawZData, zFactor])

  const originalRegionOverlay = useMemo(() => {
    if (!boundarySelectionMode || !originalRegion) return null

    const x0 = originalRegion.start.x.num
    const y0 = originalRegion.start.y.num
    const x1 = originalRegion.end.x.num
    const y1 = originalRegion.end.y.num

    const rectX0 = (shouldSwapAxes ? y0 : x0) * xFactor
    const rectY0 = (shouldSwapAxes ? x0 : y0) * yFactor
    const rectX1 = (shouldSwapAxes ? y1 : x1) * xFactor
    const rectY1 = (shouldSwapAxes ? x1 : y1) * yFactor

    return {
      type: 'rect' as const,
      x0: rectX0,
      y0: rectY0,
      x1: rectX1,
      y1: rectY1,
      fillcolor: 'rgba(128, 128, 128, 0.6)',
      line: {
        color: 'rgba(255, 255, 255, 0.8)',
        width: 2,
      },
      layer: 'above' as const,
    }
  }, [boundarySelectionMode, originalRegion, shouldSwapAxes, xFactor, yFactor])

  const plotData = useMemo((): Plotly.Data[] => {
    const traces: Plotly.Data[] = []

    if (displayZData && xPositions.length > 0 && yPositions.length > 0) {
      const heatmapTrace: Plotly.Data = {
        z: displayZData,
        customdata: rawZData,
        x: xPositions.map((v) => v * xFactor),
        y: yPositions.map((v) => v * yFactor),
        type: 'heatmap',
        colorscale: plotlyColorScale,
        showscale: true,
        reversescale: false,
        hovertemplate: `X: %{x:.2f}<br>Y: %{y:.2f}<br>Thickness: %{z:.3f} ${zDisplayUnit.name}<extra></extra>`,
        colorbar: {
          thickness: 15,
          len: 0.8,
          title: {
            text: zDisplayUnit.name,
          },
        },
      }

      // Only set zmin/zmax for fallback color scale (data-driven)
      // For proper GeckoColorScale, let it handle its own range.
      // dataMin/dataMax are in source units, so scale to the display unit.
      if (
        !colorScale ||
        typeof colorScale.getColorForValue !== 'function' ||
        colorScaleMin === undefined ||
        colorScaleMax === undefined
      ) {
        if (dataMin !== undefined && dataMax !== undefined) {
          heatmapTrace.zmin = dataMin * zFactor
          heatmapTrace.zmax = dataMax * zFactor
        }
      }

      traces.push(heatmapTrace)
    }

    if (selectedMarker) traces.push(selectedMarker)

    return traces
  }, [
    displayZData,
    rawZData,
    xPositions,
    yPositions,
    xFactor,
    yFactor,
    zFactor,
    selectedMarker,
    zDisplayUnit,
    plotlyColorScale,
    colorScale,
    colorScaleMin,
    colorScaleMax,
    dataMin,
    dataMax,
  ])

  const formattedValue = useMemo(() => {
    if (!selectedBin) return 'Click to select'
    // Stored in source units; display in the preferred unit.
    const shown = selectedBin.measurement.convertTo(zDisplayUnit)
    return shown.unit.formatNumber({
      num: shown.num,
      locale: 'en-US',
      variant: 'geometric',
    })
  }, [selectedBin, zDisplayUnit])

  if (!cdf) {
    return (
      <Container>
        <Text strong>{title}</Text>
        <Text type='secondary'>No CDF available</Text>
      </Container>
    )
  }

  return (
    <Container>
      <Text strong>{title}</Text>
      <Spin spinning={isLoading}>
        <HeatmapContainer>
          <Plot
            data={plotData}
            layout={{
              width,
              height,
              margin: { t: 10, b: 35, l: 45, r: 60 },
              xaxis: {
                showticklabels: true,
                ticks: 'outside',
                tickfont: { size: 9, color: '#999' },
                title: {
                  text: xAxisDisplayUnit?.name,
                  font: { size: 9, color: '#999' },
                },
              },
              yaxis: {
                showticklabels: true,
                ticks: 'outside',
                tickfont: { size: 9, color: '#999' },
                title: {
                  text: yAxisDisplayUnit?.name,
                  font: { size: 9, color: '#999' },
                },
              },
              plot_bgcolor: 'rgba(0, 0, 0, 0)',
              paper_bgcolor: 'rgba(0, 0, 0, 0)',
              shapes: originalRegionOverlay ? [originalRegionOverlay] : [],
            }}
            config={{
              displayModeBar: false,
              responsive: true,
            }}
            onClick={handleClick}
          />
        </HeatmapContainer>
      </Spin>
      <SelectedValueDisplay>
        <Text type='secondary'>Selected:</Text>
        <Text strong style={{ color: selectedBin ? '#4caf50' : '#999' }}>
          {formattedValue}
        </Text>
      </SelectedValueDisplay>
    </Container>
  )
}
